import { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { CanvasserType, assertNever } from '@signalvote/shared';
import type { CanvasserStatsItem, CampaignBenchmarks } from '@signalvote/shared';
import { TrendIcon, trendSortRank } from './TrendIcon';
import { SortIcon } from './SortIcon';
import type { SortDirection } from './SortIcon';
import { formatRelativeTime } from '@/lib/utils';
import { CanvasserTypeBadge } from '@/components/admin/staff/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getReliabilityColor, getSurveyColor } from './colorUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

type SortField = 'name' | 'type' | 'vendor' | 'shifts' | 'hours' | 'doors' | 'contacts' | 'lastActive' | 'noShows' | 'reliability' | 'surveyCompletion' | 'trend';

function BenchmarkIndicator({ value, benchmark }: { value: number; benchmark: number | null | undefined }) {
  if (benchmark == null || benchmark === 0) return null;
  const ratio = value / benchmark;
  if (ratio > 1.1) return <TrendingUp className="ml-1 inline h-3.5 w-3.5 text-green-600" />;
  if (ratio < 0.9) return <TrendingDown className="ml-1 inline h-3.5 w-3.5 text-red-600" />;
  return null;
}


interface CanvasserTableProps {
  canvasserStats: CanvasserStatsItem[];
  onCanvasserClick: (userId: string) => void;
  isLoading?: boolean;
  benchmarks?: CampaignBenchmarks;
}

export function CanvasserTable({ canvasserStats, onCanvasserClick, isLoading, benchmarks }: CanvasserTableProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const sortedStats = useMemo(() => {
    return [...canvasserStats].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
          break;
        case 'type':
          cmp = (a.canvasserType || '').localeCompare(b.canvasserType || '');
          break;
        case 'vendor':
          cmp = (a.vendorName || '').localeCompare(b.vendorName || '');
          break;
        case 'shifts':
          cmp = a.shiftsCompleted - b.shiftsCompleted;
          break;
        case 'hours':
          cmp = a.hoursWorked - b.hoursWorked;
          break;
        case 'doors':
          cmp = a.doorsKnocked - b.doorsKnocked;
          break;
        case 'contacts':
          cmp = a.contactsMade - b.contactsMade;
          break;
        case 'noShows':
          cmp = a.shiftsNoShow - b.shiftsNoShow;
          break;
        case 'reliability':
          cmp = (a.reliabilityRate ?? -1) - (b.reliabilityRate ?? -1);
          break;
        case 'surveyCompletion':
          cmp = (a.surveyCompletionRate ?? -1) - (b.surveyCompletionRate ?? -1);
          break;
        case 'trend':
          cmp = trendSortRank(a.trendDoorsPerHour) - trendSortRank(b.trendDoorsPerHour);
          break;
        case 'lastActive': {
          const aTime = new Date(a.lastActive || 0).getTime();
          const bTime = new Date(b.lastActive || 0).getTime();
          cmp = aTime - bTime;
          break;
        }
        default:
          assertNever(sortField);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [canvasserStats, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedStats.length / pageSize));
  // Clamp while rendering rather than correcting afterwards: a filter that
  // shrinks the list lands on the last page, with no frame of an out-of-range
  // (empty) page in between.
  const page = Math.min(currentPage, totalPages);
  const startIndex = (page - 1) * pageSize;
  const paginatedStats = sortedStats.slice(startIndex, startIndex + pageSize);

  return (
    <Card className="card-interactive">
      <CardHeader>
        <CardTitle>Canvasser List</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : canvasserStats.length === 0 ? (
          <p className="text-sm text-muted-foreground">No canvasser data</p>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('name')}>
                        Name <SortIcon field="name" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[90px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('type')}>
                        Type <SortIcon field="type" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[140px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('vendor')}>
                        Vendor <SortIcon field="vendor" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[140px] text-xs font-semibold uppercase tracking-wider">Teams</TableHead>
                    <TableHead className="w-[70px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('shifts')}>
                        Shifts <SortIcon field="shifts" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[70px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('hours')}>
                        Hours <SortIcon field="hours" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[70px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('doors')}>
                        Doors <SortIcon field="doors" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[80px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('contacts')}>
                        Contacts <SortIcon field="contacts" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[80px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('noShows')}>
                        No-Shows <SortIcon field="noShows" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[90px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('reliability')}>
                        Reliability <SortIcon field="reliability" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[90px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('surveyCompletion')}>
                        Survey % <SortIcon field="surveyCompletion" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[70px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('trend')}>
                        Trend <SortIcon field="trend" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                    <TableHead className="w-[100px]">
                      <button className="flex items-center text-xs font-semibold uppercase tracking-wider hover:text-foreground" onClick={() => handleSort('lastActive')}>
                        Last Active <SortIcon field="lastActive" activeField={sortField} direction={sortDirection} />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedStats.map((c) => (
                    <TableRow
                      key={c.userId}
                      className="cursor-pointer even:bg-muted/30 hover:bg-accent/50 transition-colors duration-150"
                      onClick={() => onCanvasserClick(c.userId)}
                    >
                      <TableCell className="font-medium">
                        {c.firstName} {c.lastName}
                      </TableCell>
                      <TableCell>
                        <CanvasserTypeBadge type={c.canvasserType} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {c.vendorName || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {c.teamNames.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {c.teamNames.map((t) => (
                              <span
                                key={t}
                                className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{c.shiftsCompleted}</TableCell>
                      <TableCell className="tabular-nums">{c.hoursWorked.toFixed(1)}</TableCell>
                      <TableCell className="tabular-nums" data-testid={`doors-cell-${c.userId}`}>
                        {c.doorsKnocked}
                        {c.hoursWorked > 0 && (
                          <BenchmarkIndicator
                            value={c.doorsKnocked / c.hoursWorked}
                            benchmark={benchmarks?.avgDoorsPerHour}
                          />
                        )}
                        {c.trendDoorsKnocked !== 'Insufficient' && (
                          <TrendIcon direction={c.trendDoorsKnocked} />
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums" data-testid={`contacts-cell-${c.userId}`}>
                        {c.contactsMade}
                        {c.shiftsCompleted > 0 && (
                          <BenchmarkIndicator
                            value={c.contactsMade / c.shiftsCompleted}
                            benchmark={benchmarks?.avgContactsPerShift}
                          />
                        )}
                        {c.trendContactsMade !== 'Insufficient' && (
                          <TrendIcon direction={c.trendContactsMade} />
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{c.shiftsNoShow}</TableCell>
                      <TableCell className="tabular-nums">
                        {c.reliabilityRate !== null ? (
                          <span className={getReliabilityColor(c.reliabilityRate)}>
                            {Math.round(c.reliabilityRate * 10) / 10}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {c.surveyCompletionRate !== null ? (
                          <span className={getSurveyColor(c.surveyCompletionRate)}>
                            {Math.round(c.surveyCompletionRate * 10) / 10}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`trend-cell-${c.userId}`}>
                        <TrendIcon direction={c.trendDoorsPerHour} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatRelativeTime(c.lastActive)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {sortedStats.length > 10 && (
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    Showing {startIndex + 1}-{Math.min(startIndex + pageSize, sortedStats.length)} of {sortedStats.length}
                  </span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(val) => {
                      setPageSize(Number(val));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-full"
                    onClick={() => setCurrentPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-full"
                    onClick={() => setCurrentPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
